import {
  getAlbumPath,
  getProjectPath,
  getExperiencePath,
  getSocialNetworkPath,
} from '../../utils/getPublicPath';

import { tags } from '../tags';
import type { Playlist, Song } from '../../interfaces/types';

export enum ProjectTypesEnum {
  WEB = 'WEB',
  MOBILE = 'MOBILE',
  SCRIPTS = 'SCRIPTS',
  PERSONAL_PROJECTS = 'PERSONAL_PROJECTS',
}

/* ============================
   Core3: Custom Project Entries
   ============================ */

/** Smart Accident Detection and Notification System for Two-Wheelers
 *  Timeline: August 2024 – Ongoing
 *  Summary: IoT-based accident detection with accelerometer; if the rider doesn’t respond (buzzer), auto-notifies emergency contacts with live GPS location via GSM. Focus on real-time monitoring, rapid response, and rider safety.
 *  Tech:
 *    • Hardware: Arduino/ESP32, MPU6050, Neo-6M GPS, SIM800 GSM, Buzzer
 *    • Software: Embedded C/C++
 *    • Communication: UART/I2C, SMS gateway
 */
export const SmartAccidentDetection = {
  name: 'Smart Accident Detection & Notification (Two-Wheelers)',
  artist: 'Mahetab Shaikh',
  length: 'Aug 2024 – Ongoing',
  description:
    "IoT safety system using MPU6050 accelerometer to detect crash events. If the rider doesn't acknowledge via buzzer, the device sends SMS with GPS coordinates (Neo-6M) over SIM800 GSM to emergency contacts. Ensures real-time monitoring, rapid response, and rider safety. Hardware: Arduino/ESP32, MPU6050, Neo-6M GPS, SIM800 GSM, Buzzer. Software: Embedded C/C++. Communication: UART/I2C, SMS gateway.",
  types: [ProjectTypesEnum.PERSONAL_PROJECTS],
  imageUrl: getProjectPath('smart_accident/1.png'),
  images: [
    getProjectPath('smart_accident/1.png'),
    // getProjectPath('smart_accident/1.png'),
    // getProjectPath('smart_accident/1.png'),
  ],
  // Add links when available:
   link: 'https://github.com/mahetab1357/accident-detetection-device',
  skills: [tags.Arduino, tags['C++']],
} as Song;

/** RK Paint Website */
export const RKPaintWebsite = {
  name: 'RK Paint — Corporate Website',
  artist: 'Core3 Innovations',
  length: '2025',
  description:
    'Responsive corporate website for RK Paint showcasing product catalogue, color ranges, dealer contact, and enquiry forms with lightweight animations and SEO-ready structure.',
  types: [ProjectTypesEnum.WEB],
  imageUrl: getProjectPath('rk_paint/1.png'),
  link: 'https://rk-enterprisess.in/', // add live URL when ready
  images: [
    getProjectPath('rk_paint/1.png'),
    getProjectPath('rk_paint/2.png'),
    getProjectPath('rk_paint/3.png'),
    getProjectPath('rk_paint/4.png'),
  ],
  skills: [tags.React, tags.Tailwind],
} as Song;

/** NGO WebApp */
export const NGOWebApp = {
  name: 'NGO Management WebApp',
  artist: 'Core3 Innovations',
  length: '2025',
  description:
    'End-to-end NGO portal with programs, volunteer onboarding, donation tracking, beneficiary records, events, and admin dashboards.',
  types: [ProjectTypesEnum.WEB],
  imageUrl: getProjectPath('ngo_webapp/cover.png'),
  link: 'https://rajshreepratishthan.org/',
  images: [
    getProjectPath('ngo_webapp/1.png'),
    getProjectPath('ngo_webapp/2.png'),
    getProjectPath('ngo_webapp/3.png'),
    getProjectPath('ngo_webapp/4.png'),
    // getProjectPath('ngo_webapp/5.png'),
    getProjectPath('ngo_webapp/6.png'),
    getProjectPath('ngo_webapp/7.png'),
    getProjectPath('ngo_webapp/8.png'),
    getProjectPath('ngo_webapp/9.png'),
    getProjectPath('ngo_webapp/10.png'),
    getProjectPath('ngo_webapp/11.png'),
    getProjectPath('ngo_webapp/12.png'),
    getProjectPath('ngo_webapp/13.png'),
    getProjectPath('ngo_webapp/14.png'),
    getProjectPath('ngo_webapp/15.png'),
    getProjectPath('ngo_webapp/16.png'),
  ],
  skills: [tags.React, tags.Tailwind],
} as Song;

/** College Website */
export const CollegeWebsite = {
  name: 'COCSIT College Website',
  artist: 'Mahetab Shaikh',
  length: '2024',
  description:
    'Modern college website with departments, admissions flow, notices, events, placements, faculty profiles, and downloadable resources.',
  types: [ProjectTypesEnum.WEB],
  imageUrl: getProjectPath('college_site/cover.jpg'),
  link: 'https://www.cocsit.org.in/',
  images: [
    getProjectPath('college_site/1.png'),
    getProjectPath('college_site/2.png'),
    getProjectPath('college_site/3.png'),
  ],
  skills: [tags.React, tags.Tailwind],
} as Song;

/** Email Generator */
export const EmailGenerator = {
  name: 'Email Generator',
  artist: 'Mahetab Shaikh',
  length: '2024',
  description:
    'Developed an AI-powered Chrome extension with a Spring Boot backend that generates professional and context-aware emails instantly. Integrated OpenAI API for smart email drafting, enabling users to write clear, effective, and personalized messages with just one click',
  types: [ProjectTypesEnum.WEB],
  imageUrl: getProjectPath('email_generator/cover.jpg'),
  link: 'https://github.com/mahetab1357/email-writer',
  images: [
    getProjectPath('email_generator/1.png'),
    getProjectPath('email_generator/2.png'),
    getProjectPath('email_generator/3.png'),
    getProjectPath('email_generator/4.png'),
  ],
  skills: [tags.SpringBoot, tags.Java],
} as Song;

/** Core3 Innovations Software Company Website */
export const Core3InnovationsSite = {
  name: 'Core3 Innovations — Software Company Website',
  artist: 'Core3 Innovations',
  length: '2025',
  description:
    'Company website outlining services (web/app/devops), training offerings, portfolio, and WhatsApp-CTA lead capture with SEO best practices.',
  types: [ProjectTypesEnum.WEB],
  imageUrl: getProjectPath('core3_site/cover.png'),
  link: 'https://www.core3innovations.com/',
  images: [
    getProjectPath('core3_site/1.png'),
    getProjectPath('core3_site/2.png'),
    getProjectPath('core3_site/3.png'),
  ],
  skills: [tags.React, tags.Tailwind],
} as Song;

/** Muskan Lingeries — Store Website */
export const MuskanLingeriesSite = {
  name: 'Muskan Lingeries — Store Website',
  artist: 'Muskan Lingeries',
  length: '2026',
  description:
    "Static storefront for a Mumbai lingerie brand with a full product catalogue, collections, and categories — 100% WhatsApp-based ordering, no backend or payment gateway required. Built with React, Vite, and GSAP for smooth scroll-triggered animations.",
  types: [ProjectTypesEnum.WEB],
  imageUrl: getProjectPath('muskan_lingeries/cover.png'),
  link: 'https://muskan-lingeries.vercel.app/',
  images: [getProjectPath('muskan_lingeries/cover.png')],
  skills: [tags.React, tags.Vite, tags.GSAP, tags.Tailwind],
} as Song;

/** Distributed URL Shortener with Real-Time Analytics */
export const UrlShortener = {
  name: 'URL Shortener with Real-Time Analytics',
  artist: 'Mahetab Shaikh',
  length: '2026',
  description:
    'Polyglot distributed system: a FastAPI service handles URL shortening and redirects behind a Redis cache, a Spring Boot service consumes click events off RabbitMQ and aggregates them into real-time analytics, and a React dashboard polls and visualizes the result. 72 automated tests (49 Python, 23 Java), a Locust load test, and a 5-job CI pipeline on GitHub Actions. Demonstrates cache-aside strategy, async event-driven decoupling, graceful degradation on service-to-service calls, and containerized orchestration with Docker Compose.',
  types: [ProjectTypesEnum.WEB],
  imageUrl: getProjectPath('url_shortener/cover.png'),
  link: 'https://github.com/mahetab1357/url-shortener',
  images: [getProjectPath('url_shortener/cover.png')],
  skills: [tags.FastAPI, tags.SpringBoot, tags.React, tags.Redis, tags.RabbitMQ, tags.Postgres, tags.Docker],
} as Song;

/* ============
   Playlist
   ============ */

export const PersonalProjects = {
  name: 'PROJECTS',
  color: '#2e3f43',
  songs: [
    UrlShortener,
    MuskanLingeriesSite,
    SmartAccidentDetection,
    RKPaintWebsite,
    NGOWebApp,
    CollegeWebsite,
    EmailGenerator,
    Core3InnovationsSite,
  ],
  filters: [ProjectTypesEnum.PERSONAL_PROJECTS, ProjectTypesEnum.WEB, ProjectTypesEnum.MOBILE],
  getImage(lang) {
    return getAlbumPath('Projects-cover', lang, 'png');
  },
} as Playlist;
